import { useState, useEffect } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useClient } from '@/contexts/ClientContext';
import { useScenarios, type ScenarioWithResult } from '@/contexts/ScenarioContext';
import { SidebarLayout, SidebarItem } from '@/components/layout/SidebarLayout';
import { SimulationConfigurator, SimulationConfig } from '@/components/simulation/SimulationConfigurator';
import { SimulationResultData } from '@/components/simulation/SimulationResults';
import { PricingSimulator } from '@/components/pricing/PricingSimulator';
import { CompetitorMoveSimulator } from '@/components/pricing/CompetitorMoveSimulator';
import { ComparisonView } from '@/components/scenarios/ComparisonView';
import { TornadoChart, generateSensitivityFactors } from '@/components/charts/TornadoChart';

// Icons
const PinIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M12 17v5M9 10.76a2 2 0 0 1-1.11 1.79l-1.78.9A2 2 0 0 0 5 15.24V17h14v-1.76a2 2 0 0 0-1.11-1.79l-1.78-.9A2 2 0 0 1 15 10.76V6h1a2 2 0 0 0 0-4H8a2 2 0 0 0 0 4h1v4.76z" />
  </svg>
);

const CompareIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="3" y="3" width="7" height="7" rx="1" />
    <rect x="14" y="3" width="7" height="7" rx="1" />
    <rect x="3" y="14" width="7" height="7" rx="1" />
    <rect x="14" y="14" width="7" height="7" rx="1" />
  </svg>
);

const CheckIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

// Window definitions for Simulations (with i18n)
const windowDefs = [
  {
    id: 'portfolio-strategy',
    icon: '📊',
    name: 'Market & Portfolio Strategy',
    nameEs: 'Estrategia de Mercado y Portafolio',
    description: 'Where to play + what to have',
    descriptionEs: 'Donde jugar + que tener',
    subItems: ['Market Entry', 'Portfolio Optimization', 'SKU Rationalization'],
    subItemsEs: ['Entrada al Mercado', 'Optimizacion de Portafolio', 'Racionalizacion de SKU'],
  },
  {
    id: 'launch-planning',
    icon: '🚀',
    name: 'Launch Planning',
    nameEs: 'Planeacion de Lanzamiento',
    description: 'Bringing new products to market',
    descriptionEs: 'Llevando nuevos productos al mercado',
    subItems: ['Product Concept', 'Pricing Strategy', 'Launch Timeline'],
    subItemsEs: ['Concepto de Producto', 'Estrategia de Precios', 'Cronograma de Lanzamiento'],
  },
  {
    id: 'optimization',
    icon: '⚙️',
    name: 'Optimization',
    nameEs: 'Optimizacion',
    description: 'Fine-tuning levers',
    descriptionEs: 'Ajuste fino de palancas',
    subItems: ['Price Optimization', 'Competitor Moves', 'Promo Optimization', 'Mix Optimization'],
    subItemsEs: ['Optimizacion de Precio', 'Movimientos de Competencia', 'Optimizacion de Promocion', 'Optimizacion de Mezcla'],
  },
  {
    id: 'execution',
    icon: '📋',
    name: 'Execution Planning',
    nameEs: 'Planeacion de Ejecucion',
    description: 'How to implement',
    descriptionEs: 'Como implementar',
    subItems: ['Action Plans', 'Resource Allocation', 'Timeline'],
    subItemsEs: ['Planes de Accion', 'Asignacion de Recursos', 'Cronograma'],
  },
];

// Helper to get sidebar items based on language
function getSidebarItems(language: string): SidebarItem[] {
  const isEs = language === 'es';
  return windowDefs.map((w) => ({
    id: w.id,
    icon: <span className="text-lg">{w.icon}</span>,
    name: isEs ? w.nameEs : w.name,
    description: isEs ? w.descriptionEs : w.description,
    subItems: (isEs ? w.subItemsEs : w.subItems).map((si, idx) => ({
      id: w.subItems[idx], // Keep English ID for internal logic
      label: si,
    })),
  }));
}

export function DecidePage() {
  const [activeWindow, setActiveWindow] = useState<string>('portfolio-strategy');
  const [activeTab, setActiveTab] = useState<string>('Market Entry');
  const { t, language } = useLanguage();
  const { selectedClient } = useClient();

  const isEs = language === 'es';
  const selectedWindow = windowDefs.find((w) => w.id === activeWindow);
  const sidebarItems = getSidebarItems(language);

  // Get the current tab label based on language
  const getTabLabel = (tabId: string): string => {
    if (!selectedWindow) return tabId;
    const tabIndex = selectedWindow.subItems.indexOf(tabId);
    if (tabIndex === -1) return tabId;
    return isEs ? selectedWindow.subItemsEs[tabIndex] : tabId;
  };

  // When window changes, reset to first tab
  const handleWindowChange = (windowId: string) => {
    setActiveWindow(windowId);
    const win = windowDefs.find((w) => w.id === windowId);
    if (win) {
      setActiveTab(win.subItems[0]);
    }
  };

  const contentHeader = selectedWindow && (
    <div className="flex items-center gap-3 mb-6">
      <span className="text-2xl">{selectedWindow.icon}</span>
      <div>
        <h2 className="text-xl font-semibold text-white">
          {isEs ? selectedWindow.nameEs : selectedWindow.name}
        </h2>
        <p className="text-sm text-text-secondary">{getTabLabel(activeTab)}</p>
      </div>
    </div>
  );

  return (
    <SidebarLayout
      title={t.simulations || 'Simulations'}
      subtitle={isEs ? 'Ejecuta simulaciones y toma decisiones basadas en datos' : 'Run simulations and make data-driven decisions'}
      items={sidebarItems}
      activeItemId={activeWindow}
      activeSubItemId={activeTab}
      onItemChange={handleWindowChange}
      onSubItemChange={setActiveTab}
      contentHeader={contentHeader}
    >
      <SimulationContent
        windowId={activeWindow}
        tab={activeTab}
        clientId={selectedClient?.client_id || null}
        language={language}
      />
    </SidebarLayout>
  );
}

interface PinnedScenario {
  id: string;
  name: string;
  config: SimulationConfig;
  result: SimulationResultData;
  pinnedAt: string;
}

function SimulationContent({
  windowId,
  tab,
  clientId,
  language,
}: {
  windowId: string;
  tab: string;
  clientId: string | null;
  language: string;
}) {
  const isEs = language === 'es';
  const [recentSimulations, setRecentSimulations] = useState<Array<{ config: SimulationConfig; result: SimulationResultData }>>([]);
  const [pinnedScenarios, setPinnedScenarios] = useState<PinnedScenario[]>([]);
  const [comparisonMode, setComparisonMode] = useState(false);
  const [selectedForComparison, setSelectedForComparison] = useState<string[]>([]);
  const [showSensitivity, setShowSensitivity] = useState(false);
  const [scenarioNameInput, setScenarioNameInput] = useState('');
  const [showPinDialog, setShowPinDialog] = useState<number | null>(null);

  // Load pinned scenarios from localStorage on mount
  useEffect(() => {
    if (clientId) {
      const stored = localStorage.getItem(`pinned-scenarios-${clientId}`);
      if (stored) {
        try {
          setPinnedScenarios(JSON.parse(stored));
        } catch (e) {
          console.error('Failed to parse pinned scenarios:', e);
        }
      }
    }
  }, [clientId]);

  // Save pinned scenarios to localStorage when they change
  useEffect(() => {
    if (clientId && pinnedScenarios.length > 0) {
      localStorage.setItem(`pinned-scenarios-${clientId}`, JSON.stringify(pinnedScenarios));
    }
  }, [clientId, pinnedScenarios]);

  if (!clientId) {
    return (
      <div className="card p-12 text-center">
        <div className="text-4xl mb-4">📊</div>
        <p className="text-text-secondary text-lg mb-2">
          {isEs ? 'Selecciona un cliente para ejecutar simulaciones' : 'Select a client to run simulations'}
        </p>
        <p className="text-text-tertiary text-sm">
          {isEs
            ? 'Elige un cliente del menu desplegable en el encabezado para acceder a las herramientas de simulacion.'
            : 'Choose a client from the dropdown in the header to access simulation tools.'}
        </p>
      </div>
    );
  }

  // Callback receives both config and result from SimulationConfigurator
  const handleSimulationComplete = (config: SimulationConfig, result: SimulationResultData) => {
    setRecentSimulations((prev) => [{ config, result }, ...prev].slice(0, 5));
  };

  // Pin a scenario for comparison
  const handlePinScenario = (idx: number, name: string) => {
    const sim = recentSimulations[idx];
    if (!sim) return;

    const newScenario: PinnedScenario = {
      id: `scenario-${Date.now()}`,
      name: name || `Scenario ${pinnedScenarios.length + 1}`,
      config: sim.config,
      result: sim.result,
      pinnedAt: new Date().toISOString(),
    };

    setPinnedScenarios((prev) => [...prev, newScenario]);
    setShowPinDialog(null);
    setScenarioNameInput('');
  };

  // Unpin a scenario
  const handleUnpinScenario = (id: string) => {
    setPinnedScenarios((prev) => prev.filter((s) => s.id !== id));
    setSelectedForComparison((prev) => prev.filter((sid) => sid !== id));
  };

  // Toggle scenario selection for comparison
  const toggleScenarioSelection = (id: string) => {
    setSelectedForComparison((prev) => {
      if (prev.includes(id)) {
        return prev.filter((sid) => sid !== id);
      }
      if (prev.length >= 3) {
        // Replace oldest selection
        return [...prev.slice(1), id];
      }
      return [...prev, id];
    });
  };

  // Get scenarios selected for comparison
  const scenariosForComparison: ScenarioWithResult[] = selectedForComparison
    .map((id) => {
      const pinned = pinnedScenarios.find((s) => s.id === id);
      if (!pinned) return null;
      return {
        id: pinned.id,
        clientId: clientId,
        name: pinned.name,
        scenarioType: 'market' as const,
        config: pinned.config as unknown as Record<string, unknown>,
        createdAt: pinned.pinnedAt,
        updatedAt: pinned.pinnedAt,
        isBaseline: false,
        version: 1,
        status: 'completed' as const,
        latestResult: {
          id: `result-${pinned.id}`,
          scenarioId: pinned.id,
          computedAt: pinned.pinnedAt,
          results: pinned.result as unknown as Record<string, unknown>,
          metrics: {
            total_combinations: pinned.result.total_combinations,
            top_share: pinned.result.top_products[0]?.share || 0,
            attributes_count: pinned.result.attributes_used,
          },
        },
      } as ScenarioWithResult;
    })
    .filter((s): s is ScenarioWithResult => s !== null);

  // Check if this is a pricing tab that should use PricingSimulator
  const isPricingTab = tab === 'Price Optimization' || tab === 'Pricing Strategy';

  // Check if this is a competitor moves tab
  const isCompetitorMovesTab = tab === 'Competitor Moves';

  // If it's a pricing tab, render the dedicated PricingSimulator
  if (isPricingTab) {
    return <PricingSimulator clientId={clientId} />;
  }

  // If it's a competitor moves tab, render the CompetitorMoveSimulator
  if (isCompetitorMovesTab) {
    return <CompetitorMoveSimulator clientId={clientId} language={language as 'en' | 'es'} />;
  }

  // Map tab names to simulation types
  const getSimulationType = (tab: string): string => {
    const typeMap: Record<string, string> = {
      'Market Entry': 'market_entry',
      'Portfolio Optimization': 'portfolio_optimization',
      'SKU Rationalization': 'sku_rationalization',
      'Product Concept': 'launch',
      'Promo Optimization': 'pricing',
      'Mix Optimization': 'portfolio_optimization',
    };
    return typeMap[tab] || 'market_entry';
  };

  // Generate unique key to force re-render when tab changes
  const simulationType = getSimulationType(tab);

  return (
    <div className="space-y-6">
      {/* Comparison Mode Toggle */}
      {pinnedScenarios.length >= 2 && (
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button
              onClick={() => setComparisonMode(!comparisonMode)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                comparisonMode
                  ? 'bg-primary-500 text-white'
                  : 'bg-surface-700 text-text-secondary hover:bg-surface-600'
              }`}
            >
              <CompareIcon />
              {isEs ? 'Comparar Escenarios' : 'Compare Scenarios'} ({selectedForComparison.length}/3)
            </button>
            {comparisonMode && selectedForComparison.length >= 2 && (
              <button
                onClick={() => setShowSensitivity(!showSensitivity)}
                className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                  showSensitivity
                    ? 'bg-amber-500 text-white'
                    : 'bg-surface-700 text-text-secondary hover:bg-surface-600'
                }`}
              >
                {isEs ? 'Analisis de Sensibilidad' : 'Sensitivity Analysis'}
              </button>
            )}
          </div>
          {comparisonMode && (
            <button
              onClick={() => {
                setComparisonMode(false);
                setSelectedForComparison([]);
              }}
              className="text-text-tertiary hover:text-text-secondary text-sm"
            >
              {isEs ? 'Salir de Comparacion' : 'Exit Comparison'}
            </button>
          )}
        </div>
      )}

      {/* Comparison View */}
      {comparisonMode && selectedForComparison.length >= 2 && (
        <div className="space-y-6">
          <ComparisonView
            scenarios={scenariosForComparison}
            onRemove={(id) => toggleScenarioSelection(id)}
          />

          {/* Sensitivity Analysis (Tornado Chart) */}
          {showSensitivity && scenariosForComparison.length > 0 && (
            <TornadoChart
              factors={generateSensitivityFactors(
                scenariosForComparison[0].latestResult?.metrics?.total_combinations as number || 1000,
                scenariosForComparison[0].config
              )}
              baselineValue={scenariosForComparison[0].latestResult?.metrics?.total_combinations as number || 1000}
              title="Sensitivity Analysis"
              valueLabel="Impact on Volume"
            />
          )}
        </div>
      )}

      {/* Pinned Scenarios Panel */}
      {pinnedScenarios.length > 0 && (
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-text-secondary">
              {isEs ? 'Escenarios Fijados' : 'Pinned Scenarios'} ({pinnedScenarios.length})
            </h3>
            {pinnedScenarios.length >= 2 && !comparisonMode && (
              <button
                onClick={() => setComparisonMode(true)}
                className="text-xs text-primary-400 hover:text-primary-300"
              >
                {isEs ? 'Comparar' : 'Compare'}
              </button>
            )}
          </div>
          <div className="flex flex-wrap gap-2">
            {pinnedScenarios.map((scenario) => (
              <div
                key={scenario.id}
                className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-all ${
                  selectedForComparison.includes(scenario.id)
                    ? 'border-primary-500 bg-primary-500/10'
                    : 'border-surface-600 bg-surface-700/50 hover:border-surface-500'
                }`}
                onClick={() => comparisonMode && toggleScenarioSelection(scenario.id)}
              >
                {comparisonMode && (
                  <div
                    className={`w-4 h-4 rounded border flex items-center justify-center ${
                      selectedForComparison.includes(scenario.id)
                        ? 'border-primary-500 bg-primary-500'
                        : 'border-surface-500'
                    }`}
                  >
                    {selectedForComparison.includes(scenario.id) && (
                      <CheckIcon />
                    )}
                  </div>
                )}
                <span className="text-white text-sm">{scenario.name}</span>
                <span className="text-xs text-text-tertiary">
                  {(scenario.result.top_products[0]?.share * 100).toFixed(1)}%
                </span>
                {!comparisonMode && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleUnpinScenario(scenario.id);
                    }}
                    className="text-text-tertiary hover:text-red-400 ml-1"
                  >
                    ×
                  </button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Simulation Configurator - handles its own results display */}
      {!comparisonMode && (
        <SimulationConfigurator
          key={`${clientId}-${simulationType}-${tab}`}
          clientId={clientId}
          simulationType={simulationType}
          onRunSimulation={handleSimulationComplete}
          language={language}
        />
      )}

      {/* Recent Simulations History with Pin Button */}
      {!comparisonMode && recentSimulations.length > 0 && (
        <div className="card p-6">
          <h3 className="text-lg font-semibold text-white mb-4">
            {isEs ? 'Simulaciones Recientes' : 'Recent Simulations'}
          </h3>
          <div className="space-y-2">
            {recentSimulations.map(({ config, result }, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-surface-700/50 rounded-lg">
                <div className="flex-1">
                  <div className="text-white font-medium">{config.name}</div>
                  <div className="text-sm text-text-secondary">
                    {result.total_combinations.toLocaleString()} {isEs ? 'combinaciones' : 'combinations'} • {isEs ? 'Share top (valor):' : 'Top value share:'} {(result.top_products[0]?.share * 100).toFixed(1)}%
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="text-xs text-text-tertiary">
                    {new Date(result.created_at).toLocaleTimeString()}
                  </div>

                  {/* Pin Scenario Button */}
                  {showPinDialog === idx ? (
                    <div className="flex items-center gap-2">
                      <input
                        type="text"
                        value={scenarioNameInput}
                        onChange={(e) => setScenarioNameInput(e.target.value)}
                        placeholder={isEs ? 'Nombre del escenario...' : 'Scenario name...'}
                        className="px-2 py-1 text-sm bg-surface-600 border border-surface-500 rounded text-white placeholder-text-tertiary w-32"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            handlePinScenario(idx, scenarioNameInput);
                          } else if (e.key === 'Escape') {
                            setShowPinDialog(null);
                            setScenarioNameInput('');
                          }
                        }}
                      />
                      <button
                        onClick={() => handlePinScenario(idx, scenarioNameInput)}
                        className="p-1 text-primary-400 hover:text-primary-300"
                        title={isEs ? 'Guardar' : 'Save'}
                      >
                        <CheckIcon />
                      </button>
                      <button
                        onClick={() => {
                          setShowPinDialog(null);
                          setScenarioNameInput('');
                        }}
                        className="p-1 text-text-tertiary hover:text-text-secondary"
                        title={isEs ? 'Cancelar' : 'Cancel'}
                      >
                        ×
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setShowPinDialog(idx)}
                      className="flex items-center gap-1 px-2 py-1 text-sm text-text-secondary hover:text-primary-400 hover:bg-surface-600 rounded transition-colors"
                      title={isEs ? 'Fijar escenario para comparar' : 'Pin scenario for comparison'}
                    >
                      <PinIcon />
                      {isEs ? 'Fijar' : 'Pin'}
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Help Text */}
      {!comparisonMode && pinnedScenarios.length === 0 && recentSimulations.length > 0 && (
        <p className="text-center text-text-tertiary text-sm">
          {isEs ? 'Fija escenarios para compararlos lado a lado' : 'Pin scenarios to compare them side-by-side'}
        </p>
      )}
    </div>
  );
}
